function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { CloseButton, GridItem, TextButton, TextField, Typography, WithInfoIcon, useStateInRef } from 'seagull';
import styles from "./AddAdditionalRecipients.module.scss";
import { useForm, FormProvider, useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { connectControllerToTextField } from "../../../utils/react-hook-form.utils";
import { reviewerEmailValidator, reviewerNameValidator } from "../../../business-logic/draftLogic";
import SignatoryCheckbox from "../SignatoryCheckbox/SignatoryCheckbox";
function AdditionalRecipientRow(props) {
  const {
    idx,
    emailRules,
    trigger,
    onRemove
  } = props;
  const {
    control
  } = useFormContext();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.recipientRow
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".firstName"),
    rules: reviewerNameValidator,
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({
      testid: "reviewer-".concat(idx, "-first-name"),
      label: "First name"
    }, connectControllerToTextField(p)))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".lastName"),
    rules: reviewerNameValidator,
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({
      testid: "reviewer-".concat(idx, "-last-name"),
      label: "Last name"
    }, connectControllerToTextField(p)))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".email"),
    rules: emailRules,
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({
      testid: "reviewer-".concat(idx, "-email"),
      label: "Email"
    }, connectControllerToTextField(p), {
      onChange: e => {
        p.field.onChange(e);
        trigger('additionalRecipients');
      }
    }))
  }), /*#__PURE__*/React.createElement(CloseButton, {
    skin: "transparent",
    "data-testid": "reviewer-".concat(idx, "-remove"),
    onClick: onRemove
  })), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".isSignatory"),
    render: p => /*#__PURE__*/React.createElement(SignatoryCheckbox, {
      infoTestid: "reviewer-".concat(idx, "-required-sign-info"),
      info: /*#__PURE__*/React.createElement("div", {
        "data-testid": "reviewer-".concat(idx, "-required-sign-info-tooltip")
      }, "All signatures are required for the proposal to take effect."),
      checkboxTestid: "reviewer-".concat(idx, "-required-sign"),
      onChange: p.field.onChange,
      value: p.field.value
    })
  }));
}
export default function AddAdditionalRecipients(props) {
  const {
    initialState,
    showReviewerErrors,
    primaryContact,
    amendmentInfoCallout
  } = props;
  const getProps = useStateInRef(props);
  const wasChangedRef = useRef(false);
  const isPrimaryASampleContact = primaryContact === null || primaryContact === void 0 ? void 0 : primaryContact.isSample();
  const form = useForm({
    mode: 'all',
    defaultValues: {
      additionalRecipients: initialState.map(r => ({
        firstName: r.firstName || '',
        lastName: r.lastName || '',
        email: r.email || '',
        isSignatory: r.isSignatory || false
      }))
    }
  });
  const recipientsControl = useFieldArray({
    control: form.control,
    name: 'additionalRecipients'
  });
  const recipients = form.watch('additionalRecipients');
  const hasRecipients = recipientsControl.fields.length > 0;
  // react-hook-form doesn't work immutably for an array when changing inputs, so we need to use the stringified version to compare changes.
  const stringifiedRecipients = JSON.stringify(form.watch('additionalRecipients'));
  const emailRules = useMemo(() => {
    const primaryAndRecipients = (primaryContact ? [{
      email: primaryContact.email()
    }] : []).concat(recipients);
    return reviewerEmailValidator(primaryAndRecipients);
  }, [primaryContact, recipients]);
  useEffect(() => {
    // We need to check for duplicate emails after removing a row, or changing primaryContact.
    form.trigger('additionalRecipients');
  }, [primaryContact, recipients.length]);
  useEffect(() => {
    if (form.formState.isDirty || wasChangedRef.current) {
      // react-hook-form doesn't work immutably for an array when changing inputs, so we need to clone the array.
      const newValue = [...form.getValues().additionalRecipients];
      getProps().onChange(newValue);
      wasChangedRef.current = true;
    }
  }, [stringifiedRecipients]);
  useEffect(() => {
    if (showReviewerErrors) {
      // Will auto-focus on the first invalid field.
      form.handleSubmit(() => {})();
    }
  }, [showReviewerErrors]);
  useEffect(() => {
    if (isPrimaryASampleContact) {
      const emptyReviewers = [];
      form.setValue('additionalRecipients', emptyReviewers);
      getProps().onChange(emptyReviewers);
    }
  }, [isPrimaryASampleContact]);
  const AddReviewer = /*#__PURE__*/React.createElement(TextButton, {
    underline: "none",
    "data-testid": "add-reviewer",
    onClick: () => recipientsControl.append({
      firstName: '',
      lastName: '',
      email: '',
      isSignatory: false
    })
  }, hasRecipients ? '+ Add an additional recipient' : '+ Add additional recipients');
  const AdditionalRecipientsWithInfoIon = _ref => {
    let {
      children
    } = _ref;
    return /*#__PURE__*/React.createElement(WithInfoIcon, {
      info: "Additional recipients get a link to view the proposal. They can add a payment method but don\u2019t need to approve it unless marked as 'required to sign'. They aren\u2019t added to the business but can be later by the primary."
    }, children);
  };
  return /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement(GridItem, {
    className: cn(styles.root, {
      [styles.adjustUpwards]: !hasRecipients && !amendmentInfoCallout
    }),
    span: 12
  }, amendmentInfoCallout, hasRecipients && /*#__PURE__*/React.createElement(AdditionalRecipientsWithInfoIon, null, /*#__PURE__*/React.createElement(Typography, {
    weight: "normal"
  }, "Additional recipients")), recipientsControl.fields.map((r, idx) => /*#__PURE__*/React.createElement(AdditionalRecipientRow, {
    key: r.id,
    idx: idx,
    setFocus: form.setFocus,
    trigger: form.trigger,
    emailRules: emailRules,
    onRemove: () => recipientsControl.remove(idx)
  })), !isPrimaryASampleContact && recipientsControl.fields.length < 10 && (hasRecipients ? /*#__PURE__*/React.createElement(React.Fragment, null, AddReviewer) : /*#__PURE__*/React.createElement(AdditionalRecipientsWithInfoIon, null, AddReviewer))), hasRecipients && /*#__PURE__*/React.createElement(GridItem, {
    className: styles.separator,
    span: 12
  }));
}